import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL, TOKEN_KEY, storage } from '../api/client';
import api from '../api/endpoints';
import { connectRealtime, onRealtime, watchTrip, unwatchTrip } from '../services/realtime';
import {
  bearing, haversineM, lengthM, remainingRoute, routeBetween, snapToRoute,
} from '../utils/routeGeo';
import { colors } from '../theme';
import TopDownBike from './TopDownBike';
import TopDownCar from './TopDownCar';

const TILE = 256;
const worldSize = (zoom) => TILE * (2 ** zoom);
const project = ([lng, lat], zoom) => {
  const size = worldSize(zoom);
  const x = ((lng + 180) / 360) * size;
  const sin = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size;
  return { x, y };
};
const fitView = (points, width, height) => {
  const pts = points.filter(Boolean);
  if (!pts.length) return null;
  const lngs = pts.map((p) => p[0]); const lats = pts.map((p) => p[1]);
  const center = [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
  let zoom = 17;
  for (; zoom > 3; zoom -= 1) {
    const a = project([Math.min(...lngs), Math.max(...lats)], zoom);
    const b = project([Math.max(...lngs), Math.min(...lats)], zoom);
    if (Math.abs(b.x - a.x) <= width * 0.7 && Math.abs(b.y - a.y) <= height * 0.7) break;
  }
  return { center, zoom };
};
const ago = (ms) => {
  if (!ms) return 'no update yet';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)} min ago`;
};
const fmtDist = (m) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);

/**
 * Who is moving and where to, in the viewer's words.
 *   delivery - a rider between the shop and the customer
 *   ride/courier - a driver coming to the pickup, then going to the drop
 */
const wordsFor = (ride, who) => {
  const delivery = !!ride && ride.serviceType === 'delivery';
  const cab = !!ride && ride.serviceType === 'ride';
  return {
    mover: who === 'rider' ? 'You are' : (delivery ? 'Rider is' : 'Driver is'),
    first: delivery
      ? (who === 'shop' ? 'your counter' : 'the shop')
      : (who === 'rider' ? 'the pickup' : (cab ? 'you' : 'the pickup')),
    second: delivery
      ? (who === 'rider' ? 'the customer' : 'you')
      : (who === 'rider' ? 'the drop' : (cab ? 'your drop' : 'the drop')),
    waiting: delivery ? 'Waiting for the rider’s first location…' : 'Waiting for the driver’s first location…',
    finding: delivery ? 'Finding a rider…' : 'Finding a driver…',
  };
};

/** A rider this far from every segment of the drawn road has left it. */
const OFF_ROUTE_M = 60;
/** Never ask Google for a new road more often than this, whatever the GPS does. */
const REROUTE_MIN_GAP_MS = 8000;
/** Pings arrive every ~5 s; the bike takes a little less to cover each one, so it never stalls. */
const RUN_MS = 4500;

/**
 * Swiggy-style trip map for the browser.
 *
 * One static base image; on top, the road the rider is coming by (Google's
 * route via the server) as an SVG line, and a bike that runs along that road
 * between pings, turning with each bend. The line shrinks as the rider covers
 * it and is fetched again when the rider leaves it, so a detour shows up as a
 * new route within one ping. Pings come from the socket when up and from a
 * 5 s poll always.
 */
const LiveTripMap = ({ ride, who = 'customer', selfPosition = null, height = 240, style }) => {
  const box = useRef(null);
  const bikeEl = useRef(null);
  const [width, setWidth] = useState(0);
  const [base, setBase] = useState(null);
  const [failed, setFailed] = useState(false);
  const [rider, setRider] = useState(() => (ride && ride.driverLocation && ride.driverLocation.coordinates
    ? { coordinates: ride.driverLocation.coordinates, heading: ride.driverLocation.heading || 0, at: ride.driverLocation.updatedAt ? new Date(ride.driverLocation.updatedAt).getTime() : null }
    : null));
  const [route, setRoute] = useState(null);
  const [routing, setRouting] = useState(false);
  const [drawnFrom, setDrawnFrom] = useState(null);
  const [, tick] = useState(0);
  const lastSnap = useRef(null);
  const lastRouteAt = useRef(0);
  const routeReq = useRef(0);
  const anim = useRef(null);            // current requestAnimationFrame id
  const bikeAt = useRef(null);          // { x, y, heading } as last drawn
  const runKey = useRef('');            // coords of the ping the bike last ran to

  const rideId = ride && ride._id;
  const pickup = ride && ride.pickup && ride.pickup.coordinates;
  const drop = ride && ride.drop && ride.drop.coordinates;
  const token = storage.get(TOKEN_KEY);
  const beforePickup = !!ride && ['accepted', 'arrived'].includes(ride.status);
  const finding = !!ride && ride.status === 'requested';
  const words = wordsFor(ride, who);
  const isCar = !['bike', 'courier'].includes((ride && ride.vehicleType) || 'bike');
  const target = beforePickup ? pickup : drop;
  const targetKey = target ? `${target[0]},${target[1]}` : '';

  useEffect(() => {
    const measure = () => setWidth(box.current ? box.current.clientWidth : 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (selfPosition) setRider((r) => ({ coordinates: selfPosition, heading: (r && r.heading) || 0, at: Date.now() }));
  }, [selfPosition]);
  useEffect(() => {
    if (!rideId) return undefined;
    connectRealtime().then(() => watchTrip(rideId));
    const off = onRealtime('driver:location', (p) => {
      if (String(p.rideId) !== String(rideId) || !p.coordinates) return;
      setRider({ coordinates: p.coordinates, heading: p.heading || 0, at: p.at || Date.now() });
    });
    return () => { off(); unwatchTrip(rideId); };
  }, [rideId]);

  /* ------------------------------ the road ------------------------------ */
  const fetchRoute = useCallback(async (from, to) => {
    if (!from || !to) return;
    const seq = routeReq.current + 1;
    routeReq.current = seq;
    lastRouteAt.current = Date.now();
    setRouting(true);
    try {
      const { data } = await api.maps.route(from, to);
      if (seq !== routeReq.current || !data || !data.points || !data.points.length) return;
      setRoute({ ...data, target: `${to[0]},${to[1]}` });
      lastSnap.current = null;
    } catch (e) {
      // Keep whatever road we had; the straight glide still works without one.
    } finally {
      if (seq === routeReq.current) setRouting(false);
    }
  }, []);

  useEffect(() => {
    if (!target) return;
    const from = (rider && rider.coordinates) || (beforePickup ? null : pickup);
    if (!from) return;
    if (route && route.target === targetKey) return;
    fetchRoute(from, target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, !!(rider && rider.coordinates), !!route]);

  /* ------------------------------ the frame ----------------------------- */
  const inFrame = (view, c) => {
    if (!view || !c) return false;
    const p = project(c, view.zoom); const o = project(view.center, view.zoom);
    return Math.abs(p.x - o.x) < width * 0.45 && Math.abs(p.y - o.y) < height * 0.45;
  };
  useEffect(() => {
    if (!width || !token || !pickup || !drop) return;
    const c = rider && rider.coordinates;
    // The frame follows the leg in progress: rider, next stop and the road still
    // ahead. It tightens as that shrinks, so the last few hundred metres are
    // seen up close rather than as a dot in the corner of a 3 km frame.
    const pts = (route && route.points) || [];
    const ahead = pts.length && c ? remainingRoute(pts, snapToRoute(pts, c)) : pts;
    const focus = c && target ? [c, target, ...ahead] : [pickup, drop, c, ...ahead];
    const view = fitView(focus, width, height);
    if (!view) return;
    const roadFits = ahead.every((p) => inFrame(base, p));
    if (base && inFrame(base, c) && roadFits && view.zoom <= base.zoom) return;
    const w = Math.min(Math.round(width), 640); const h = Math.min(Math.round(height), 640);
    setBase({ ...view, url: `${API_URL}/maps/static?center=${view.center[0]},${view.center[1]}&zoom=${view.zoom}&w=${w}&h=${h}&token=${token}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, token, targetKey, pickup && pickup[0], pickup && pickup[1], drop && drop[0], drop && drop[1], rider && rider.coordinates && rider.coordinates[0], rider && rider.coordinates && rider.coordinates[1], route && route.points && route.points.length]);

  const toScreen = useCallback((c) => {
    if (!base || !c) return null;
    const p = project(c, base.zoom); const o = project(base.center, base.zoom);
    return { x: width / 2 + (p.x - o.x), y: height / 2 + (p.y - o.y) };
  }, [base, width, height]);

  /* ------------------------------- the bike ----------------------------- */
  const paint = (x, y, heading) => {
    bikeAt.current = { x, y, heading };
    if (!bikeEl.current) return;
    bikeEl.current.style.transform = `translate(${x - 20}px, ${y - 20}px)`;
    const icon = bikeEl.current.firstElementChild && bikeEl.current.firstElementChild.nextElementSibling;
    if (icon) icon.style.transform = `rotate(${heading}deg)`;
  };

  useEffect(() => {
    const c = rider && rider.coordinates;
    if (!c || !base) return undefined;
    const points = route && route.points;
    const snap = points ? snapToRoute(points, c) : null;
    const onRoad = !!snap && snap.distM <= OFF_ROUTE_M;

    if (!onRoad && target && Date.now() - lastRouteAt.current > REROUTE_MIN_GAP_MS) fetchRoute(c, target);

    const prev = lastSnap.current;
    const path = onRoad && prev && prev.route === points ? routeBetween(points, prev, snap) : null;
    const dest = onRoad ? snap.point : c;
    const legs = path && path.length > 1 ? path : [dest];
    const from = (prev && prev.point) || c;
    const startSnap = prev && prev.route === points ? prev : snap;
    lastSnap.current = onRoad ? { ...snap, route: points } : { point: c, index: -1, t: 0, route: null };

    if (anim.current) cancelAnimationFrame(anim.current);
    // Same ping, new frame or new road: place the bike, don't run the leg again.
    const key = `${c[0]},${c[1]}`;
    const rerun = runKey.current === key;
    runKey.current = key;
    if (!prev || rerun) {
      const s = toScreen(dest);
      paint(s.x, s.y, rider.heading || 0);
      setDrawnFrom(onRoad ? snap : null);
      return undefined;
    }

    // Run along the bends at constant speed, finishing just before the next ping.
    setDrawnFrom(onRoad ? startSnap : null);
    const stops = [from, ...legs];
    const lens = legs.map((p, i) => haversineM(stops[i], p));
    const total = lens.reduce((a, b) => a + b, 0) || 1;
    const t0 = performance.now();
    const step = (now) => {
      let elapsed = Math.min(1, (now - t0) / RUN_MS) * total;
      let i = 0;
      while (i < lens.length - 1 && elapsed > lens[i]) { elapsed -= lens[i]; i += 1; }
      const f = lens[i] ? Math.min(1, elapsed / lens[i]) : 1;
      const a = toScreen(stops[i]); const b = toScreen(stops[i + 1]);
      const hdg = bearing(stops[i], stops[i + 1]);
      paint(a.x + (b.x - a.x) * f, a.y + (b.y - a.y) * f, hdg == null ? (bikeAt.current ? bikeAt.current.heading : 0) : hdg);
      if (now - t0 < RUN_MS) anim.current = requestAnimationFrame(step);
      else setDrawnFrom(onRoad ? snap : null);
    };
    anim.current = requestAnimationFrame(step);
    return () => { if (anim.current) cancelAnimationFrame(anim.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rider && rider.coordinates && rider.coordinates[0], rider && rider.coordinates && rider.coordinates[1], route && route.points, base]);

  // A new frame re-projects everything; put the bike straight where it is.
  useEffect(() => {
    const s = toScreen((lastSnap.current && lastSnap.current.point) || (rider && rider.coordinates));
    if (s) paint(s.x, s.y, bikeAt.current ? bikeAt.current.heading : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const roadPoints = useMemo(() => {
    if (!route || !route.points || !base) return '';
    return remainingRoute(route.points, drawnFrom).map(toScreen).map((s) => `${s.x},${s.y}`).join(' ');
  }, [route, drawnFrom, base, toScreen]);

  const pPx = toScreen(pickup); const dPx = toScreen(drop);

  const line = useMemo(() => {
    const c = rider && rider.coordinates;
    if (finding) return route ? `${words.finding} ${route.distanceKm} km trip · ~${route.durationMin} min` : words.finding;
    if (!c) return words.waiting;
    const label = beforePickup ? words.first : words.second;
    if (!target) return `Updated ${ago(rider.at)}`;
    let m = haversineM(c, target);
    let mins = null;
    if (route && route.points && route.target === targetKey) {
      const snap = snapToRoute(route.points, c);
      if (snap && snap.distM <= OFF_ROUTE_M) {
        const ahead = lengthM(remainingRoute(route.points, snap));
        const total = lengthM(route.points) || 1;
        m = ahead + snap.distM;
        if (route.durationMin) mins = Math.max(1, Math.round(route.durationMin * (ahead / total)));
      }
    }
    const stale = rider.at && Date.now() - rider.at > 60000;
    const eta = mins ? ` · ~${mins} min` : '';
    return `${words.mover} ${fmtDist(m)} from ${label}${eta} · ${stale ? '⚠️ last seen ' : ''}${ago(rider.at)}`;
  }, [rider, beforePickup, finding, words, target, targetKey, route]);

  const fresh = rider && rider.at && Date.now() - rider.at < 60000;
  const pin = (px, color, label) => (px ? (
    <div style={{ position: 'absolute', left: px.x - 14, top: px.y - 34, width: 28, textAlign: 'center', pointerEvents: 'none' }}>
      <div style={{ width: 26, height: 26, borderRadius: 13, background: color, color: '#fff', fontWeight: 800, fontSize: 12, lineHeight: '22px', border: '2px solid #fff', margin: '0 auto' }}>{label}</div>
      <div style={{ width: 0, height: 0, margin: '0 auto', borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `8px solid ${color}` }} />
    </div>
  ) : null);

  return (
    <div ref={box} style={{ position: 'relative', height, borderRadius: 16, overflow: 'hidden', background: colors.primaryLight, ...style }}>
      {base && !failed ? (
        <img src={base.url} alt="Route" onError={() => setFailed(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : null}
      {failed ? <div className="t-small" style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>Map unavailable</div> : null}
      {roadPoints ? (
        <svg width={width} height={height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <polyline points={roadPoints} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points={roadPoints} fill="none" stroke="#1B4DE4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
      {pin(pPx, colors.success, 'P')}
      {pin(dPx, colors.danger, 'D')}
      {rider && rider.coordinates && base ? (
        <div ref={bikeEl} style={{
          position: 'absolute', left: 0, top: 0, width: 40, height: 40, pointerEvents: 'none',
          transform: 'translate(-999px, -999px)', display: 'grid', placeItems: 'center',
        }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: 20, background: 'rgba(27,77,228,0.18)' }} />
          <div style={{ transition: 'transform .15s linear' }}>{isCar ? <TopDownCar /> : <TopDownBike />}</div>
        </div>
      ) : null}
      <div style={{
        position: 'absolute', left: 8, right: 8, bottom: 8, display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(255,255,255,0.94)', borderRadius: 999, padding: '6px 10px', pointerEvents: 'none',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: fresh ? colors.success : colors.warning, flex: '0 0 auto' }} />
        <span className="t-tiny" style={{ fontWeight: 700, color: colors.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{line}</span>
        {routing ? <span className="t-tiny" style={{ color: colors.primary, flex: '0 0 auto' }}>rerouting…</span> : null}
      </div>
    </div>
  );
};

export default LiveTripMap;
